/**
 * CHOOSING A PDF — the checks that can be made before a byte is uploaded.
 *
 * Lifted out of `PracticeDocumentSettingsComponent` when the inline editor became two dialogs
 * (`tasks/practice-documents.md` Phase 8). Both dialogs pick a PDF and both must refuse exactly
 * what the server refuses, so the checks live in ONE object rather than being copied into two
 * components that would drift the first time either was edited.
 *
 * ── WHY THE FILE IS CHECKED HERE AS WELL AS ON THE SERVER ────────────────────────────────────
 *
 * The server re-inspects everything (§3.1: the A/B/C decision is made once, server-side, at upload)
 * and is the authority. These checks exist so a 40 MB scan costs a sentence instead of an upload
 * and a refusal — and the content check exists because the extension is a claim while `%PDF-` is
 * evidence. A `.docx` renamed `.pdf` is the case that makes the difference. Nothing here is ever
 * allowed to be LOOSER than the server: a file this screen accepts and the server then refuses has
 * already cost the upload, which is the whole point of checking.
 *
 * A refusal is held on the object, against the picker that produced it — never handed to a toast.
 * It is the instruction for what to do next and it has to survive being read twice.
 */

import { PracticeDocumentService } from '../../../core/services/practice-document.service';

/**
 * The server's `DEFAULT_MAX_BYTES` / `DEFAULT_MAX_PAGES` in `utils/practiceDocumentInspection.js`,
 * restated so a screen can name them before a byte is uploaded.
 *
 * The contract Phase 1c was written against named the caps but not their values, and there is no
 * endpoint that publishes them, so these two numbers CAN drift — the server reads
 * `PRACTICE_DOCUMENT_MAX_BYTES` / `PRACTICE_DOCUMENT_MAX_PAGES` from the environment per call.
 *
 * Two things keep that from becoming a lie on the screen. This copy is never allowed to be LOOSER
 * than the server — a file accepted here and refused there has cost the upload, which is the whole
 * point of checking, and the first draft of this file said 50 pages against a server that refuses
 * at 25, which is exactly that mistake. And a refusal that names its own ceiling replaces these
 * numbers for the rest of the session: `inspectOrRefuse` puts `maxBytes` and `maxPages` in
 * `details` on every 413/422 precisely so a client can re-quote the truth. See `applyServerCap`.
 */
export const MAX_DOCUMENT_FILE_BYTES = 20 * 1024 * 1024;
export const MAX_DOCUMENT_PAGES = 25;

export class PracticeDocumentPdfChoice {

  /** The accepted file, or `null` while nothing has been accepted. */
  file: File | null = null;

  /** Best-effort, and `null` is a real answer — see `PracticeDocumentService.readPageCount`. */
  pageCount: number | null = null;

  /** Why the last chosen file was refused. Rendered against the picker, never as a toast. */
  error: string | null = null;

  maxFileBytes = MAX_DOCUMENT_FILE_BYTES;
  maxPages = MAX_DOCUMENT_PAGES;

  get maxFileMb(): number {
    return Math.round(this.maxFileBytes / (1024 * 1024));
  }

  clear(): void {
    this.file = null;
    this.pageCount = null;
    this.error = null;
  }

  /**
   * Name, then size, then the bytes themselves — in that order.
   *
   * A `.pdf`-named Word document passes the first check and fails the third, which is the whole
   * reason the third exists. The page count is read best-effort and an unknown count is never a
   * refusal: the server stays the authority and this only ever catches what it is sure about.
   */
  async choose(file: File): Promise<void> {
    this.clear();

    const name = (file && file.name) || 'that file';

    if (!PracticeDocumentService.looksLikePdfByName(file)) {
      this.refuse(`Only a PDF can be uploaded, and "${name}" is not one. Export or scan the `
        + 'document as a PDF and try again.');
      return;
    }

    if (file.size > this.maxFileBytes) {
      this.refuse(
        `"${name}" is ${PracticeDocumentPdfChoice.megabytes(file.size)} MB, which is more than a `
        + `practice document accepts (${this.maxFileMb} MB) — scan at a lower resolution, or split `
        + 'the document.');
      return;
    }

    let bytes: Uint8Array;
    try {
      bytes = new Uint8Array(await file.arrayBuffer());
    } catch {
      this.refuse(`"${name}" could not be read from this device. Try choosing it again.`);
      return;
    }

    if (!PracticeDocumentService.hasPdfHeader(bytes)) {
      this.refuse(`"${name}" is named like a PDF but its contents are something else. Open it, `
        + 'export it as a PDF, and try again.');
      return;
    }

    const pages = PracticeDocumentService.readPageCount(bytes);
    if (pages !== null && pages > this.maxPages) {
      this.refuse(`"${name}" has ${pages} pages, which is more than a practice document accepts `
        + `(${this.maxPages} pages) — split it and upload the parts as separate documents.`);
      return;
    }

    this.file = file;
    this.pageCount = pages;
    this.error = null;
  }

  /**
   * A refusal that names its own ceiling replaces the number the screen quotes.
   *
   * The client's constants are a statement of the server's caps that can drift (see
   * `MAX_DOCUMENT_FILE_BYTES`). When the server disagrees it says so in `details`, and from that
   * point the screen quotes the server rather than continuing to promise something it will refuse.
   */
  applyServerCap(error: any): void {
    const api = PracticeDocumentService.asApiError(error);
    const details = api && api.details;
    if (!details) return;

    // `maxBytes` is the key `inspectOrRefuse` actually sends; `maxFileBytes` is tolerated because
    // it is the obvious other name for the same number and costs nothing to accept.
    const bytes = typeof details.maxBytes === 'number' ? details.maxBytes : details.maxFileBytes;
    if (typeof bytes === 'number' && bytes > 0) {
      this.maxFileBytes = bytes;
    }
    if (typeof details.maxPages === 'number' && details.maxPages > 0) {
      this.maxPages = details.maxPages;
    }
  }

  /** The server's own words where it gave any; a plain sentence where it did not. */
  static messageFor(error: any, fallback: string): string {
    const api = PracticeDocumentService.asApiError(error);
    return api && api.message ? api.message : fallback;
  }

  static megabytes(bytes: number): string {
    return (bytes / (1024 * 1024)).toFixed(1);
  }

  /**
   * A blank with no name is a settings row nobody can find again. The file name is the obvious
   * first suggestion and the practice can overwrite it before uploading.
   */
  static nameFromFileName(fileName: string): string {
    const parts = fileName.split('.');
    const stem = parts.length > 1 ? parts.slice(0, -1).join('.') : fileName;
    return stem.replace(/[_-]+/g, ' ').trim().slice(0, 300);
  }

  private refuse(message: string): void {
    this.file = null;
    this.pageCount = null;
    this.error = message;
  }
}
